import type { ApiError } from './client'

// Human messages for the governed-write refusals the T2b backend returns.
// The server is authoritative; these only translate its problem `type`s.
export const SUPER_ADMIN_REQUIRED = 'Super admin required.'
export const SEED_MANAGED =
  'Managed by the seed file; edit via a reviewed PR.'

const TYPE_MESSAGES: Record<string, string> = {
  not_super_admin: SUPER_ADMIN_REQUIRED,
  self_deactivation: 'You cannot deactivate yourself.',
  last_super_admin:
    'Refused: this would leave the platform without an active super admin.',
  already_admin: 'That user is already a platform admin.',
  seed_managed: SEED_MANAGED,
  csrf_mismatch: 'Security check failed (CSRF). Reload the page and try again.',
}

/**
 * Fallback text for the stable envelope `code` every REST problem now carries (same
 * mapping as the MCP ErrorEnvelope). Used only when the server sent no detail text.
 */
const CODE_MESSAGES: Record<string, string> = {
  unauthorized: 'Not signed in.',
  forbidden: 'Forbidden.',
  connector_denied: 'Denied by connector policy.',
  not_found: 'Not found.',
  conflict: 'Conflict.',
  bad_request: 'Invalid request.',
  connection_required: 'A connected account is required first.',
  rate_limited: 'Rate limited — wait a moment and try again.',
  quota_exceeded: 'Quota exceeded.',
  upstream_error: 'The upstream service failed.',
  service_unavailable: 'Service unavailable — try again shortly.',
}

function asApiError(err: unknown): Partial<ApiError> {
  return (err ?? {}) as Partial<ApiError>
}

/** The problem's stable machine code (e.g. `forbidden`, `connector_denied`), if any. */
export function errorCode(err: unknown): string | undefined {
  const c = asApiError(err).code
  return typeof c === 'string' && c ? c : undefined
}

/** True for a 403 that is a connector-policy / tool-session-scope denial, not RBAC. */
export function isConnectorDenied(err: unknown): boolean {
  const e = asApiError(err)
  return e.status === 403 && e.code === 'connector_denied'
}

/** True for a 403 that means "your tier is too low for this write" (super admin). */
export function isSuperAdminRefusal(err: unknown): boolean {
  const e = asApiError(err)
  return e.status === 403 && (e.requiredTier === 'super_admin' || e.type === 'not_super_admin')
}

/**
 * True for a 403 that means the caller is no longer a platform admin at all, so the
 * Not-Authorized view is the right place (as for a 403 on any read). Tier, D5 and
 * CSRF refusals are NOT this, nor is a `connector_denied` policy denial: they are shown
 * inline next to the write.
 */
export function isAccessLost(err: unknown): boolean {
  const e = asApiError(err)
  if (e.status !== 403 || isSuperAdminRefusal(err) || isConnectorDenied(err)) return false
  return e.requiredTier === 'platform_admin' || e.type === 'forbidden'
}

function detailText(detail: unknown): string | null {
  if (typeof detail === 'string' && detail) return detail
  if (Array.isArray(detail)) {
    // Pydantic validation list: [{loc, msg, ...}]
    const msgs = detail
      .map((d) => (d && typeof d === 'object' && 'msg' in d ? String((d as { msg: unknown }).msg) : null))
      .filter(Boolean)
    if (msgs.length) return msgs.join('; ')
  }
  return null
}

/** ` [code]` suffix so the stable machine code is visible next to the message. */
function codeTag(e: Partial<ApiError>): string {
  return e.code ? ` [${e.code}]` : ''
}

/** One-line message for a failed write. */
export function writeErrorMessage(err: unknown): string {
  const e = asApiError(err)
  if (isSuperAdminRefusal(err)) return SUPER_ADMIN_REQUIRED
  if (e.type && TYPE_MESSAGES[e.type]) return TYPE_MESSAGES[e.type]
  const text = detailText(e.detail)
  const fallback = (e.code && CODE_MESSAGES[e.code]) || null
  let msg: string
  if (e.status === 422) msg = `Rejected: ${text ?? 'invalid request.'}`
  else if (e.status === 409) msg = text ?? 'Conflict.'
  else msg = text ?? fallback ?? `Request failed (${e.status ?? '?'}).`
  return msg + codeTag(e)
}

/**
 * Message for a failed write that keeps the server's problem title — used where the
 * title itself is the signal (exposure: 409 "ToS Review Required", 422 "Categories
 * Required"; upstream: "Upstream Refused", "Tool Name Collision"). Shows the stable
 * `code` next to the status (e.g. "Forbidden (403 connector_denied): …") and adds the
 * remedy.
 */
export function titledErrorMessage(err: unknown): string {
  const e = asApiError(err)
  if (isSuperAdminRefusal(err)) return SUPER_ADMIN_REQUIRED
  if (e.type && TYPE_MESSAGES[e.type]) return TYPE_MESSAGES[e.type]
  const text =
    detailText(e.detail) ??
    ((e.code && CODE_MESSAGES[e.code]) || null) ??
    `Request failed (${e.status ?? '?'}).`
  const status = [e.status, e.code].filter((v) => v !== undefined && v !== '').join(' ')
  const head = e.title
    ? `${e.title}${status ? ` (${status})` : ''}: ${text}`
    : text + codeTag(e)
  return e.remedy ? `${head} ${e.remedy}` : head
}
